import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../../../foundation/altzero_base/base/components/ui/card';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { billEmailService } from '../../services/billEmailService';
import { rowFlags, type DailyIntakeRow } from '../../lib/dailyIntake';
import { InboxGapCheck } from './InboxGapCheck';

const OPEN_KEY = 'bv-daily-intake-open';
const FOUND_TIP = 'Counted from the ingest queue (Gmail day, Pacific time); days synced before the queue show —';

const readOpen = (): boolean => {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
};

const writeOpen = (open: boolean): void => {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // storage unavailable: the section just forgets its state
  }
};

const num = (n: number | null): string => (n == null ? '0' : String(n));

type Column = { label: string; title?: string; cell: (r: DailyIntakeRow) => string };

const COLUMNS: Column[] = [
  { label: 'Found in Gmail', title: FOUND_TIP, cell: (r) => (r.found == null ? '—' : String(r.found)) },
  { label: 'Stored', cell: (r) => num(r.stored) },
  { label: 'Assigned', cell: (r) => num(r.assigned) },
  { label: 'Held', cell: (r) => num(r.held) },
  { label: 'Unmatched', cell: (r) => num(r.unmatched) },
  { label: 'Failed to ingest', cell: (r) => num(r.queue_failed) },
  { label: 'PDFs parsed', cell: (r) => num(r.pdfs_parsed) },
  { label: 'Parse failed', cell: (r) => num(r.pdfs_failed) },
  { label: 'In progress', cell: (r) => num(r.pdfs_in_progress) },
  { label: 'Waiting', cell: (r) => num(r.pdfs_waiting) },
];

type DailyIntakeTableProps = {
  /** Starts a sync of a day range (the Inbox page's Sync now). */
  onSyncRange: (range: { from: string; to: string }) => Promise<void> | void;
};

export const DailyIntakeTable = ({ onSyncRange }: DailyIntakeTableProps) => {
  const [open, setOpen] = useState<boolean>(readOpen);
  const [rows, setRows] = useState<DailyIntakeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await billEmailService.getDailyIntake(30);
        if (!mounted) return;
        if (res.success) setRows(res.data?.rows ?? []);
        else setError(res.error || 'Failed to load daily intake');
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => { mounted = false; };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    writeOpen(next);
  };

  return (
    <Card className="border-0 shadow-sm mb-4" data-testid="daily-intake">
      <CardContent className="py-3">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex w-full items-center gap-2 text-left text-sm font-medium text-gray-900"
        >
          {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          Daily email intake
          <span className="text-xs font-normal text-gray-400">last 30 days, UTC</span>
        </button>

        {open && loading && <div className="mt-3 h-20 animate-pulse rounded bg-gray-100" />}
        {open && !loading && error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {open && !loading && !error && rows.length === 0 && (
          <p className="mt-3 text-sm text-gray-500">No emails in the last 30 days.</p>
        )}
        {open && !loading && !error && rows.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-gray-500">
                  <th className="py-2 pr-3 font-medium">Day (UTC)</th>
                  {COLUMNS.map((c) => (
                    <th key={c.label} title={c.title} className="px-2 py-2 text-right font-medium">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const flags = rowFlags(r);
                  return (
                    <tr
                      key={r.day}
                      className={cn('border-b last:border-0', (flags.missing || flags.parseFailed) && 'bg-amber-50')}
                    >
                      <td className="py-1.5 pr-3 tabular-nums text-gray-700">{r.day}</td>
                      {COLUMNS.map((c) => (
                        <td key={c.label} className="px-2 py-1.5 text-right tabular-nums text-gray-700">{c.cell(r)}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {open && <InboxGapCheck onSyncRange={onSyncRange} />}
      </CardContent>
    </Card>
  );
};
